import { useGetMediaFoldersQuery } from "@api/media-folders";
import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import { useMediaLibWebsiteStoreShallow } from "@editor/modules/media-lib";
import { MediaLibCategoryEnum } from "@editor/modules/media-lib/types";
import { useEffect, useMemo, useState } from "react";
import {
  useMediaLibPanelImportImages,
  useMediaLibPanelUploadFiles,
} from "../hooks";
import { useMediaLibPanelStoreShallow } from "../stores";

interface IProps {
  fromPanel?: "images" | "videos";
}

export const MediaLibPanelNoFiles = ({}: IProps) => {
  const [canShow, setCanShow] = useState(false);
  const { category } = useMediaLibPanelStoreShallow((s) => ({
    category: s.category,
  }));

  const { canImportFromWebsite, setWebsiteUrl } =
    useMediaLibWebsiteStoreShallow((s) => ({
      canImportFromWebsite: s.websiteUrl == null && !s.isImporting,
      setWebsiteUrl: s.setWebsiteUrl,
    }));

  const { data: allFolders = [] } = useGetMediaFoldersQuery({
    archived: false,
  });

  const { openFilePicker } = useMediaLibPanelUploadFiles({
    accept: "image/*,video/mp4",
    multiple: true,
  });

  const { openImportFilePicker } = useMediaLibPanelImportImages();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCanShow(true);
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const { title, description, btnAction, btnIcon, btnLabel, shouldShow } =
    useMemo(() => {
      const data = {
        title: "You don’t have any files",
        description: "Let’s add some of your images and videos!",
        btnLabel: "Upload media",
        btnIcon: "ri-upload-cloud-2-line",
        btnAction: null as null | (() => void),
        shouldShow: true,
      };

      if (category === MediaLibCategoryEnum.Website) {
        data.title = "Looks like it’s empty here";
        data.description =
          "Let’s bring in some media from your website to get started.";
        data.btnLabel = "Import media";
        data.btnIcon = "ri-global-line";

        data.btnAction = () => {
          if (!canImportFromWebsite) return;
          const btn = document.querySelector("#media-lib-upload-btn");
          if (!(btn instanceof HTMLButtonElement)) return;
          setWebsiteUrl("");
          btn.click();
        };
      } else if (category === MediaLibCategoryEnum.Import) {
        data.title = "Looks like it’s empty here";
        data.description =
          "Let’s bring in your content by importing a PPT or PDF file.";
        data.btnLabel = "Upload file";
        data.btnIcon = "ri-upload-cloud-2-line";

        data.btnAction = openImportFilePicker;
      } else {
        data.btnLabel = "Upload media";
        data.btnAction = openFilePicker;
      }

      const isWebsiteOrImport = [
        MediaLibCategoryEnum.Website,
        MediaLibCategoryEnum.Import,
      ].includes(category);

      if (isWebsiteOrImport) {
        data.shouldShow =
          allFolders.find((el) => el.parentId === category && !el.archived) ==
          null;
      }

      return data;
    }, [
      category,
      openFilePicker,
      openImportFilePicker,
      setWebsiteUrl,
      canImportFromWebsite,
      allFolders,
    ]);

  if (!canShow || !shouldShow) {
    return null;
  }

  return (
    <div className="tw-mt-6 tw-flex tw-flex-col tw-items-center tw-text-center">
      <img
        alt="No results found"
        src="/assets/images/media-files-circle.svg"
        className="tw-mb-2 tw-size-[87px]"
      />
      <div className="tw-mb-2 tw-text-lg tw-font-bold">{title}</div>
      <div className="tw-text-[13px]">{description}</div>

      {btnAction && (
        <Button
          variant="secondary"
          size="sm"
          onClick={btnAction}
          disabled={!canImportFromWebsite}
          className="tw-my-6"
        >
          <Icon icon={btnIcon} className="tw-mr-1.5" />
          {btnLabel}
        </Button>
      )}
    </div>
  );
};
